/**
 * Timezone utilities for converting browser-local times to the tenant's configured timezone.
 * 
 * The core problem: `new Date()` returns the browser's local timezone.
 * If a staff member is in India (IST) but the business runs in Toronto (EST/EDT),
 * all business logic (cutoffs, start dates, etc.) must use Toronto time.
 */

const DEFAULT_TIMEZONE = 'America/Toronto';

// Global tenant timezone — set by TimezoneProvider on app load.
// All utility functions use this as default, so callers don't need to pass tz manually.
let _tenantTimezone = DEFAULT_TIMEZONE;

/**
 * Set the global tenant timezone. Called by TimezoneProvider when settings are loaded.
 * After this, all getNowInTimezone(), getTodayInTimezone(), etc. automatically use it.
 */
export const setGlobalTimezone = (tz) => {
  if (tz && typeof tz === 'string') {
    _tenantTimezone = tz;
  }
};

/**
 * Get the current global tenant timezone.
 */
export const getGlobalTimezone = () => _tenantTimezone;

/**
 * Get "now" as a Date object whose local methods (getHours, getDate, etc.)
 * reflect the tenant's timezone instead of the browser's timezone.
 * 
 * @param {string} tz - IANA timezone string (defaults to tenant timezone)
 * @returns {Date} Date object in the tenant's timezone
 */
export const getNowInTimezone = (tz) => {
  const effectiveTz = tz || _tenantTimezone;
  const now = new Date();
  const str = now.toLocaleString('en-US', { timeZone: effectiveTz });
  return new Date(str);
};

/**
 * Get today's date as YYYY-MM-DD string in the tenant's timezone.
 * 
 * @param {string} tz - IANA timezone string
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getTodayInTimezone = (tz) => {
  const effectiveTz = tz || _tenantTimezone;
  const now = getNowInTimezone(effectiveTz);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get tomorrow's date as YYYY-MM-DD string in the tenant's timezone.
 * 
 * @param {string} tz - IANA timezone string
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getTomorrowInTimezone = (tz) => {
  const effectiveTz = tz || _tenantTimezone;
  const now = getNowInTimezone(effectiveTz);
  now.setDate(now.getDate() + 1);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get the current hour (0-23) in the tenant's timezone.
 * 
 * @param {string} tz - IANA timezone string
 * @returns {number} Current hour in tenant timezone
 */
export const getCurrentHourInTimezone = (tz) => {
  return getNowInTimezone(tz || _tenantTimezone).getHours();
};

/**
 * SAFE conversion of a Date object to YYYY-MM-DD string.
 * 
 * NEVER use `date.toISOString().split('T')[0]` — it converts to UTC first,
 * which shifts the date if the local/tenant timezone is behind UTC.
 * 
 * @param {Date} date - A Date object (its local year/month/day are used)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const toDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a UTC/ISO datetime string for display in the tenant's timezone.
 * 
 * @param {string} utcDateTimeString - ISO datetime string
 * @param {string} tz - IANA timezone string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeInTimezone = (utcDateTimeString, tz, options = {}) => {
  const effectiveTz = tz || _tenantTimezone;
  if (!utcDateTimeString) return 'N/A';

  try {
    let dateStr = utcDateTimeString;
    if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
      dateStr = dateStr + 'Z';
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return utcDateTimeString;

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: effectiveTz,
      ...options,
    };

    return date.toLocaleString('en-US', defaultOptions);
  } catch {
    return utcDateTimeString;
  }
};

/**
 * Format a date-only string (YYYY-MM-DD) for display.
 * Does NOT do timezone conversion since these are calendar dates.
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {object} options - Intl.DateTimeFormat options override
 * @returns {string} Formatted date string (e.g., "Jan 15, 2025")
 */
export const formatDateDisplay = (dateStr, options = {}) => {
  if (!dateStr) return 'N/A';
  try {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options,
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format a time string (from an ISO datetime) in the tenant's timezone.
 * 
 * @param {string} isoString - ISO datetime string
 * @param {string} tz - IANA timezone string
 * @returns {string} Formatted time string (e.g., "02:30 PM")
 */
export const formatTimeInTimezone = (isoString, tz) => {
  const effectiveTz = tz || _tenantTimezone;
  if (!isoString) return '--:--';

  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '--:--';

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: effectiveTz,
    });
  } catch {
    return '--:--';
  }
};

/**
 * Convert a YYYY-MM-DD date string into a Date object representing
 * midnight of that date in the tenant's timezone.
 * 
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} tz - IANA timezone string
 * @returns {Date} Date object
 */
export const parseDateInTimezone = (dateStr, tz) => {
  const effectiveTz = tz || _tenantTimezone;
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    timeZone: effectiveTz,
  }).formatToParts(d);
  
  const p = {};
  parts.forEach(({ type, value }) => { p[type] = value; });
  return new Date(Number(p.year), Number(p.month) - 1, Number(p.day));
};

export { DEFAULT_TIMEZONE };
